// src/components/common/ResponsiveMetricsTable.tsx
//
// Shared mobile transformation for a Dirección analytics comparison table:
// the desktop `<Table>` becomes one card per row at phone width, instead of
// squeezing the same columns into an unreadable strip (F-A-011). Same visual
// language as this app's own datagrid card recipe (`CustomDataGridMobile`):
// a hairline-bordered card, the identity as the title, the rest as
// label/value pairs. The component also owns its loading skeleton, so a
// caller cannot forget it (code-health: "a component owns its states").
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export interface MetricsColumn<T> {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right';
  /** Card form: the value goes on its own full-width line under the label
   *  (free text, e.g. a comment) instead of right-aligned beside it. */
  block?: boolean;
  render: (row: T) => React.ReactNode;
}

export interface ResponsiveMetricsTableFooter {
  label: React.ReactNode;
  getValue: (columnKey: string) => React.ReactNode;
}

export interface ResponsiveMetricsTableProps<T> {
  columns: MetricsColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** First fetch in flight: renders placeholder rows/cards instead of `rows`. */
  loading?: boolean;
  /** Trailing emphasised row (e.g. a chain average), rendered after `rows`. */
  footer?: ResponsiveMetricsTableFooter;
  /** Extra styling for the desktop `TableContainer` (e.g. the standalone
   *  frame a table not already wrapped in a card needs). */
  containerSx?: object;
  skeletonRowCount?: number;
  /** Always render the card form, e.g. inside a narrow side panel on a wide screen. */
  forceCards?: boolean;
}

function DesktopSkeletonRows({ columnCount, rowCount }: { columnCount: number; rowCount: number }) {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: columnCount }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton variant="text" width={c === 0 ? '70%' : '50%'} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function MobileSkeletonCards({ theme, count }: { theme: Theme; count: number }) {
  return (
    <Stack spacing={1.25}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: 0 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Skeleton variant="text" width="50%" height={24} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="65%" />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

export function ResponsiveMetricsTable<T>({
  columns,
  rows,
  getRowKey,
  loading = false,
  footer,
  containerSx,
  skeletonRowCount = 4,
  forceCards = false,
}: ResponsiveMetricsTableProps<T>) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = forceCards || isPhone;

  if (!isMobile) {
    return (
      <TableContainer sx={containerSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align ?? 'left'}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <DesktopSkeletonRows columnCount={columns.length} rowCount={skeletonRowCount} />
            ) : (
              <>
                {rows.map((row) => (
                  <TableRow key={getRowKey(row)}>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align ?? 'left'}>
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {footer && (
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${theme.palette.divider}` } }}>
                    <TableCell>{footer.label}</TableCell>
                    {columns.slice(1).map((col) => (
                      <TableCell key={col.key} align={col.align ?? 'left'}>
                        {footer.getValue(col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (loading) {
    return <MobileSkeletonCards theme={theme} count={Math.min(skeletonRowCount, 3)} />;
  }

  const [titleCol, ...restCols] = columns;

  const renderCardBody = (title: React.ReactNode, values: { col: MetricsColumn<T>; value: React.ReactNode }[]) => (
    <>
      <Typography variant="body2" fontWeight={700}>
        {title}
      </Typography>
      <Divider sx={{ my: 1, opacity: 0.6 }} />
      <Stack spacing={0.5}>
        {values.map(({ col, value }) =>
          col.block ? (
            <Box key={col.key} sx={{ pt: 0.5 }}>
              <Typography variant="body2" sx={{ color: theme.custom.text.label }}>
                {col.label}
              </Typography>
              <Box sx={{ minWidth: 0, mt: 0.25 }}>{value}</Box>
            </Box>
          ) : (
            <Box key={col.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: theme.custom.text.label, flexShrink: 0 }}>
                {col.label}
              </Typography>
              <Box sx={{ minWidth: 0, textAlign: 'right' }}>{value}</Box>
            </Box>
          )
        )}
      </Stack>
    </>
  );

  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Card key={getRowKey(row)} sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: 0 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {renderCardBody(
              titleCol.render(row),
              restCols.map((col) => ({ col, value: col.render(row) }))
            )}
          </CardContent>
        </Card>
      ))}
      {footer && (
        <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: 0, backgroundColor: theme.custom.table.headerBg }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            {renderCardBody(
              footer.label,
              restCols.map((col) => ({ col, value: footer.getValue(col.key) }))
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

export default ResponsiveMetricsTable;
